import { Shield, Lock, AlertCircle } from 'lucide-react';
import AnonymityInfographic from './AnonymityInfographic';

const PrivacyPage = () => {
  return (
    <div className="max-w-4xl mx-auto">
      {/* Hero Section */}
      <div className="text-center bg-white dark:bg-gray-800 p-8 md:p-12 rounded-xl shadow-lg border dark:border-gray-700 mb-8">
        <div className="flex justify-center mb-4">
          <Shield className="w-16 h-16 text-blue-600 dark:text-blue-400" />
        </div>
        <h1 className="text-3xl md:text-5xl font-extrabold mb-4">
          Your Privacy is Protected
        </h1>
        <p className="text-md md:text-lg max-w-2xl mx-auto text-gray-700 dark:text-gray-300">
          We take your anonymity seriously. Learn how we protect your identity while still giving you control over your reports.
        </p>
      </div>

      {/* Main Explanation Section */}
      <div className="bg-white dark:bg-gray-800 p-6 md:p-8 rounded-xl shadow-lg border dark:border-gray-700 mb-8">
        <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
          <Lock className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          How Your Anonymity Works
        </h2>
        
        <div className="space-y-6 text-gray-700 dark:text-gray-300">
          <div>
            <h3 className="text-xl font-semibold mb-3 text-gray-900 dark:text-gray-100">
              No Identity Information Stored
            </h3>
            <p className="leading-relaxed">
              When you submit a report, we <strong>never</strong> collect or store any information that could identify you. 
              No email addresses, no usernames, no IP addresses, no tracking cookies. Your report is completely anonymous 
              from the moment you hit submit.
            </p>
          </div>

          <div>
            <h3 className="text-xl font-semibold mb-3 text-gray-900 dark:text-gray-100">
              Local Storage for Deletion Control
            </h3>
            <p className="leading-relaxed">
              You might wonder: "If you don't know who I am, how can I delete my report?" The answer is clever and secure. 
              When you submit a report, we generate a unique secret key that's stored <strong>only on your device</strong>  
              in your browser's local storage. This key proves you own the report without revealing your identity to us.
            </p>
          </div>

          <div>
            <h3 className="text-xl font-semibold mb-3 text-gray-900 dark:text-gray-100">
              Device-Specific Deletion
            </h3>
            <p className="leading-relaxed">
              Because the secret key is stored locally on your device, you can only delete reports from the same device 
              and browser you used to create them. This is a security feature, not a limitation—it ensures that only you 
              can delete your reports, even though we don't know who you are.
            </p>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
                  Important to Know
                </h4>
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  If you clear your browser data or switch devices, you won't be able to delete your previous reports. 
                  However, your anonymity remains completely protected—we still have no way to identify you.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Infographic: Submission Flow */}
      <div className="mb-8">
        <AnonymityInfographic type="submission" />
      </div>

      {/* Infographic: Deletion Flow */}
      <div className="mb-8">
        <AnonymityInfographic type="deletion" />
      </div>

      {/* Reassurance Section */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 p-6 md:p-8 rounded-xl shadow-lg border border-blue-200 dark:border-blue-800 mb-8">
        <h2 className="text-2xl md:text-3xl font-bold mb-4 text-center text-gray-900 dark:text-gray-100">
          Your Anonymity is Always Protected
        </h2>
        <p className="text-center text-gray-700 dark:text-gray-300 leading-relaxed max-w-2xl mx-auto">
          Throughout the entire process—from submission to deletion—we never collect any information that could 
          identify you. The system is designed from the ground up to protect your privacy while still giving you 
          control over your contributions. You can report with confidence.
        </p>
      </div>
    </div>
  );
};

export default PrivacyPage;
